import React, { useEffect, useMemo, useRef, useState } from "react";

type Props = {
    durationMs?: number;
    paused?: boolean;

    /** Render size in pixels. Laat leeg voor responsive (100%). */
    size?: number;

    /** Afstand van de services t.o.v. de kern (0..1 van viewBox). Default 0.30 */
    orbitRadius?: number;

    /** Schaal van service-iconen (1 = default). */
    iconScale?: number;

    /** Kleur voor kern + pulse (default: goud) */
    goldColor?: string;
};

const VB = 380;

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        if (!mq) return;
        const onChange = () => setReduced(!!mq.matches);
        onChange();
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);
    return reduced;
}

function easeInOut(t: number) {
    return t * t * (3 - 2 * t);
}

function clamp01(n: number) {
    return Math.max(0, Math.min(1, n));
}

function polar(cx: number, cy: number, r: number, deg: number) {
    const a = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

export default function QualityHubAnimation({
    durationMs = 9000,
    paused = false,
    size,
    orbitRadius = 0.30,
    iconScale = 1,
    goldColor = "#C9A227",
}: Props) {
    const reduced = usePrefersReducedMotion();

    const [phase, setPhase] = useState(0);
    const [active, setActive] = useState(0);
    const rafRef = useRef<number | null>(null);

    const cx = VB / 2;
    const cy = VB / 2;

    const nodes = useMemo(() => {
        const r = VB * orbitRadius;
        const angles = [-70, 170, 50];
        return angles.map((deg, i) => ({ i, ...polar(cx, cy, r, deg) }));
    }, [orbitRadius, cx, cy]);

    const safeDuration = Number.isFinite(durationMs) && durationMs > 0 ? durationMs : 9000;
    const nodeCount = nodes.length;

    useEffect(() => {
        // Reduced motion: geen animatie, vaste state
        if (reduced) {
            setPhase(1);
            setActive(0);
            return;
        }

        let start = performance.now();

        const tick = (now: number) => {
            if (!paused) {
                const elapsed = (now - start) % safeDuration;
                const t = elapsed / safeDuration;

                setPhase(t);
                setActive(Math.floor(t * 3) % 3);
            }
            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [safeDuration, paused, reduced]);

    const segT = (phase * 3) % 1;
    const pulse = easeInOut(clamp01((segT - 0.10) / 0.55));
    const pulseOpacity = 0.75 * (1 - Math.abs(segT - 0.45) / 0.45);

    const safeActive =
        Number.isFinite(active) && nodeCount > 0
            ? ((active % nodeCount) + nodeCount) % nodeCount
            : 0;

    const activeNode = nodes[safeActive];

    const highlightIn = clamp01((segT - 0.62) / 0.20);
    const highlightOut = 1 - clamp01((segT - 0.90) / 0.14);
    const checkGlow = easeInOut(highlightIn * highlightOut);
    const checkStroke = checkGlow > 0.05 ? goldColor : "currentColor";
    const checkOpacity = 0.85 + 0.15 * checkGlow;

    const svgSizing =
        typeof size === "number"
            ? { width: size, height: size }
            : { width: "100%", height: "100%" as const };

    return (
        <svg
            viewBox={`0 0 ${VB} ${VB}`}
            {...svgSizing}
            style={{ display: "block" }}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Animatie: drie diensten rondom een kwaliteitskern."
        >
            <defs>
                <filter id="qhGlow" x="-80%" y="-80%" width="260%" height="260%">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>

                <filter id="qhSoftShadow" x="-40%" y="-40%" width="180%" height="180%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="b" />
                    <feOffset dy="2" result="o" />
                    <feComponentTransfer>
                        <feFuncA type="linear" slope="0.22" />
                    </feComponentTransfer>
                    <feMerge>
                        <feMergeNode />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            {/* Orbit ring */}
            <circle
                cx={cx}
                cy={cy}
                r={VB * orbitRadius}
                fill="none"
                stroke="currentColor"
                strokeWidth={6}
                opacity={0.10}
            />

            {/* Verbinding */}
            {nodes.map((n) => (
                <line
                    key={`line-${n.i}`}
                    x1={n.x}
                    y1={n.y}
                    x2={cx}
                    y2={cy}
                    stroke="currentColor"
                    strokeWidth={4}
                    opacity={0.08}
                    strokeLinecap="round"
                />
            ))}

            {/* Pulse */}
            {!reduced && activeNode && (
                <line
                    x1={activeNode.x}
                    y1={activeNode.y}
                    x2={cx + (activeNode.x - cx) * (1 - pulse)}
                    y2={cy + (activeNode.y - cy) * (1 - pulse)}
                    stroke={goldColor}
                    strokeWidth={6}
                    opacity={Math.max(0, pulseOpacity) * 0.45}
                    strokeLinecap="round"
                    filter="url(#qhGlow)"
                />
            )}

            {/* Service icons */}
            {nodes.map((n) => {
                const isActive = n.i === safeActive;
                const breathe = reduced
                    ? 1
                    : 1 + 0.05 * Math.sin((phase * 2 + n.i * 0.7) * Math.PI * 2);
                const opacity = isActive ? 0.98 : 0.72;

                return (
                    <g
                        key={`node-${n.i}`}
                        transform={`translate(${n.x} ${n.y}) scale(${breathe * iconScale})`}
                        style={{ transformOrigin: "center" }}
                        filter="url(#qhSoftShadow)"
                        opacity={opacity}
                    >
                        {n.i === 0 && <IconWindow />}
                        {n.i === 1 && <IconKoppeling />}
                        {n.i === 2 && <IconCube />}
                    </g>
                );
            })}

            {/* Kwaliteitskern: alleen fill + check */}
            <g transform={`translate(${cx} ${cy})`} filter="url(#qhSoftShadow)">
                <circle r={24} fill={goldColor} opacity={0.30} />
                <g opacity={checkOpacity}>
                    <IconCheck stroke={checkStroke} />
                </g>
            </g>
        </svg>
    );
}

/** Icons */
function IconWindow() {
    return (
        <g>
            <rect x="-18" y="-14" width="36" height="26" rx="6" fill="none" stroke="currentColor" strokeWidth={3} />
            <line x1="-18" y1="-7" x2="18" y2="-7" stroke="currentColor" strokeWidth={3} />
            <circle cx="-12" cy="-10" r="2.2" fill="currentColor" opacity={0.9} />
            <circle cx="-5" cy="-10" r="2.2" fill="currentColor" opacity={0.9} />
            <circle cx="2" cy="-10" r="2.2" fill="currentColor" opacity={0.9} />
        </g>
    );
}

function IconKoppeling() {
    const scale = 56 / 87;
    const w = 87 * scale;
    const h = 70 * scale;

    return (
        <g transform={`translate(${-w / 2} ${-h / 2}) scale(${scale})`}>
            <path
                fill="currentColor"
                d="m 30.459171,69.449014 c -0.28205,-0.30308 -0.51282,-0.72763 -0.51282,-0.94345 0,-0.98529 -1.0582,-3.1177 -2.140261,-4.31291 -0.97518,-1.07715 -1.571545,-1.46559 -3.683672,-2.39938 -3.856115,-1.70482 -6.647015,-1.70872 -9.122642,-0.0128 -1.603852,1.09876 -2.048057,0.92053 -4.912284,-1.97092 -1.9649246,-1.98361 -2.5472166,-2.71651 -2.5472166,-3.2061 0,-0.34905 0.398003,-1.36155 0.88445,-2.24999 0.849794,-1.55205 0.884203,-1.703969 0.878144,-3.877253 -0.006,-2.179969 -0.04682,-2.352892 -1.124804,-4.773525 -1.585737,-3.560819 -3.11095,-4.997724 -6.141887,-5.786294 C 1.2790124,39.719399 0.51291636,39.362982 0.33374036,39.124358 0.09074336,38.800734 0.00606236,37.774601 4.6535785e-4,35.085916 -0.00823464,30.8788 0.08467536,30.655176 2.0599824,30.132057 c 3.124652,-0.827501 4.823217,-2.520228 6.344321,-6.322522 0.816263,-2.040408 0.904872,-2.471747 0.904872,-4.404764 0,-2.040732 -0.04373,-2.223663 -0.917654,-3.838342 -0.722602,-1.335105 -0.882129,-1.838365 -0.750526,-2.367702 0.22188,-0.892436 4.5224716,-5.257861 5.3917666,-5.47305 0.504025,-0.124766 1.077316,0.0522 2.456135,0.758168 1.702211,0.871547 1.913535,0.920492 3.941649,0.912945 2.198455,-0.0082 3.128204,-0.282557 6.152518,-1.815663 2.023805,-1.025919 3.865087,-3.42727 4.260837,-5.55688 0.0946,-0.509106 0.41688,-1.172832 0.71614,-1.474946 C 31.081121,0.023278 31.257811,0 34.729511,0 c 4.17453,0 4.30722,0.06183 4.85409,2.261906 0.5625,2.262984 2.27617,4.464804 4.13662,5.314948 0.53184,0.243024 1.53457,0.696598 2.22832,1.00794 1.89569,0.850777 2.15853,1.313718 2.15853,3.801895 0,1.696727 -0.0838,2.188908 -0.45049,2.646465 l -0.45049,0.562091 -8.21712,0.130977 c -7.76713,0.123802 -8.30754,0.161157 -9.86811,0.682092 -5.25671,1.754764 -9.273102,5.190215 -11.575534,9.901222 -1.470651,3.00909 -1.986035,5.264307 -1.986035,8.690473 0,3.431349 0.516821,5.687903 1.990393,8.690481 1.917066,3.906251 4.635614,6.650639 8.505089,8.585929 3.889927,1.945525 4.779987,2.084255 13.522767,2.107815 l 7.62643,0.0205 0.45155,0.56209 c 0.36906,0.45942 0.45155,0.94799 0.45155,2.67482 0,1.97581 -0.0421,2.15823 -0.64861,2.815 -0.35672,0.38625 -1.46412,1.03786 -2.46089,1.44801 -2.94771,1.21293 -4.76443,3.17612 -5.39821,5.83342 -0.52232,2.1899 -0.67944,2.26191 -4.93593,2.26191 -3.55366,0 -3.71058,-0.0206 -4.20426,-0.55107 z m 28.10192,-4.36263 c -3.24371,-1.1603 -5.14106,-5.31731 -3.90967,-8.56584 l 0.46156,-1.21762 -1.41313,-1.63952 c -0.77723,-0.901736 -1.63714,-1.769651 -1.91095,-1.928706 -0.3618,-0.210169 -2.60109,-0.291483 -8.19588,-0.29762 -8.77885,-0.0096 -8.58025,0.03357 -8.83747,-1.920154 -0.20823,-1.581607 0.34116,-2.777223 1.36405,-2.96856 0.43143,-0.0807 4.50045,-0.113869 9.04224,-0.0737 9.67323,0.08554 8.64835,-0.20557 11.78247,3.346774 l 2.04808,2.321378 1.2406,-0.147195 c 4.80529,-0.570142 8.63527,4.278863 7.08979,8.976093 -0.55244,1.67907 -2.3682,3.5482 -3.98822,4.10544 -1.40432,0.48304 -3.43788,0.48698 -4.77347,0.009 z m 4.09847,-3.6481 c 2.13835,-1.31622 1.89274,-4.39776 -0.44854,-5.6275 -1.35561,-0.71203 -3.05385,-0.11668 -3.96353,1.3895 -1.67673,2.77621 1.62871,5.95125 4.41207,4.238 z m 15.40974,-20.005495 c -1.34241,-0.500647 -2.66578,-2.334028 -3.68955,-3.158913 l -0.34093,-0.654763 h -23.56818 c -10.13038,0 -19.91085,0.184716 -20.33651,-0.01108 -0.76602,-0.352339 -1.22584,-1.855181 -1.02349,-3.217396 0.27848,-1.874625 5.04708,-2.307328 21.13633,-1.77153 h 23.73346 l 0.7101,-1.153169 c 1.28946,-2.094006 3.22892,-3.136688 5.81707,-3.127318 3.42208,0.01238 6.18322,2.625441 6.46061,6.114131 0.15154,1.905882 -0.23028,3.265894 -1.32888,4.733274 -1.73524,2.317725 -4.87838,3.250599 -7.57003,2.246761 z m 4.15996,-3.802425 c 0.8973,-0.710415 1.23747,-1.382208 1.24746,-2.463552 0.0174,-1.887336 -1.26057,-3.262544 -3.03058,-3.261096 -3.39566,0.0028 -4.50692,4.136864 -1.60635,5.97591 0.95196,0.603569 2.45004,0.492517 3.38947,-0.251262 z M 35.382251,23.628579 c -0.94248,-0.770426 -0.94554,-3.161557 -0.005,-4.110769 0.44454,-0.448779 0.813,-0.470133 8.11165,-0.470133 4.20526,0 7.79717,-0.05858 7.982,-0.130181 0.18483,-0.0716 1.26872,-1.150055 2.40866,-2.39657 l 2.0726,-2.266387 -0.38461,-1.202203 c -1.50446,-4.702569 1.56737,-9.124819 6.3372,-9.123131 3.75826,0.0014 6.60201,2.872116 6.60332,6.666086 0.002,4.08766 -3.02169,6.959921 -7.10248,6.748068 -1.47108,-0.07637 -1.57713,-0.04516 -2.15179,0.633422 -2.5758,3.041615 -4.91358,5.359498 -5.74365,5.694786 -0.78713,0.317945 -2.22124,0.376112 -9.27304,0.376112 -7.61169,0 -8.38677,-0.03669 -8.85459,-0.4191 z m 28.19209,-10.267971 c 1.9049,-1.172639 1.90641,-4.132769 0.003,-5.391932 -2.07055,-1.369579 -4.85336,0.108379 -4.85577,2.578916 -0.003,2.543606 2.72368,4.123811 4.85299,2.813016 z"
            />
        </g>
    );
}

function IconCube() {
    return (
        <g transform="translate(0 1)">
            <path
                d="M 0 -16 L 16 -8 L 16 10 L 0 18 L -16 10 L -16 -8 Z"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                strokeLinejoin="round"
            />
            <path d="M 0 -16 L 0 18" stroke="currentColor" strokeWidth={3} strokeLinecap="round" />
            <path d="M -16 -8 L 0 0 L 16 -8" fill="none" stroke="currentColor" strokeWidth={3} strokeLinejoin="round" />
        </g>
    );
}

function IconCheck({ stroke }: { stroke: string }) {
    return (
        <g>
            <path
                d="M -7 1 L -2 6 L 9 -6"
                fill="none"
                stroke={stroke}
                strokeWidth={4}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </g>
    );
}
